/**
 * Universal sharing React Query hooks.
 *
 * Replaces per-entity sharing hooks (the interaction's, since deleted with
 * its route in #12394; useViewSharing, useShareModal) with a single set of
 * hooks that call the universal sharing endpoints.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EntityType,
  SharePayload,
  ShareUserInput,
  ShareTeamInput,
  SharedUser,
  SharedTeam,
  SharingStatusResponse,
  BulkSharePayload,
  NotifySharePayload,
} from "@/services/sharing";
import {
  getSharingStatus,
  updateSharing,
  bulkShare,
  notifyShare,
} from "@/services/sharing";
import { refreshCachedView } from "@/components/FpPages/RMS/Data/views";
import {
  dashboardsQueryKey,
  setCachedDashboard,
} from "@/components/FpPages/RMS/Dashboards/data";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const SHARING_QUERY_KEYS = {
  all: ["sharing"] as const,
  status: (entityType: EntityType, entityId: string) =>
    [...SHARING_QUERY_KEYS.all, entityType, entityId] as const,
};

// ---------------------------------------------------------------------------
// Optimistic update helper
// ---------------------------------------------------------------------------

/**
 * Compute the optimistic SharingStatusResponse from the current cache
 * and the outgoing SharePayload. Resolves names for newly added
 * users/teams from the available_users/available_teams lists.
 */
function computeOptimisticStatus(
  current: SharingStatusResponse,
  payload: SharePayload
): SharingStatusResponse {
  // Build lookup maps from current available + current shared for name resolution
  const userMap = new Map<string, SharedUser>();
  for (const u of current.users) userMap.set(u.id, u);
  for (const u of current.available_users) userMap.set(u.id, u);

  const teamMap = new Map<string, SharedTeam>();
  for (const t of current.teams) teamMap.set(t.id, t);
  for (const t of current.available_teams) teamMap.set(t.id, t);

  // Resolve new users list
  const newUsers: SharedUser[] = payload.users.map((u: ShareUserInput) => {
    const existing = userMap.get(u.id);
    return {
      id: u.id,
      name: existing?.name ?? "Unknown User",
      type: u.type,
      email: existing?.email,
    };
  });

  // Resolve new teams list
  const newTeams: SharedTeam[] = payload.teams.map((t: ShareTeamInput) => {
    const existing = teamMap.get(t.id);
    return {
      id: t.id,
      name: existing?.name ?? "Unknown Team",
      type: t.type,
    };
  });

  // Compute new available lists (remove anyone who's now shared)
  const sharedUserIds = new Set(newUsers.map((u) => u.id));
  const sharedTeamIds = new Set(newTeams.map((t) => t.id));

  // Merge: anyone who was shared but is no longer goes back to available
  const removedUsers = current.users.filter((u) => !sharedUserIds.has(u.id));
  const removedTeams = current.teams.filter((t) => !sharedTeamIds.has(t.id));

  const newAvailableUsers = [
    ...current.available_users.filter((u) => !sharedUserIds.has(u.id)),
    ...removedUsers.map((u) => ({ ...u, type: "view" as const })),
  ];
  const newAvailableTeams = [
    ...current.available_teams.filter((t) => !sharedTeamIds.has(t.id)),
    ...removedTeams.map((t) => ({ ...t, type: "view" as const })),
  ];

  // Org state
  const hasOrg =
    payload.organization !== undefined
      ? !!payload.organization
      : !!current.org_id;
  const orgId = hasOrg ? (current.org_id ?? "org") : null;
  const orgPermission = payload.organization
    ? payload.organization.type
    : hasOrg
      ? current.org_permission
      : null;

  // Link sharing state
  const linkSharing =
    payload.link_sharing !== undefined
      ? payload.link_sharing
      : current.link_sharing;

  // Access inherited from another entity (#11941) is not touched by a save
  // and keeps the entity readable beyond its own grants.
  const isPrivate =
    newUsers.length === 0 &&
    newTeams.length === 0 &&
    !hasOrg &&
    !linkSharing &&
    !current.inherited;

  return {
    ...current,
    users: newUsers,
    teams: newTeams,
    available_users: newAvailableUsers,
    available_teams: newAvailableTeams,
    org_id: orgId,
    org_permission: orgPermission,
    is_private: isPrivate,
    link_sharing: linkSharing,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch the sharing status of any entity.
 *
 * @param entityType - "workspace" | "view" | "interaction"
 * @param entityId - The entity's ID (null disables the query)
 * @param includeAvailable - When true, includes available users/teams (expensive)
 */
export function useSharingStatus(
  entityType: EntityType,
  entityId: string | null,
  includeAvailable: boolean = false
) {
  return useQuery({
    ...sharingStatusQueryOptions(entityType, entityId ?? "", includeAvailable),
    enabled: !!entityId,
  });
}

/**
 * The sharing-status read, as one query: the hook above and a caller that
 * fetches it outside a component (`queryClient.fetchQuery`) share its key and
 * freshness, so a fresh answer one reads is the other's.
 */
export const sharingStatusQueryOptions = (
  entityType: EntityType,
  entityId: string,
  includeAvailable: boolean = false
) => ({
  queryKey: [
    ...SHARING_QUERY_KEYS.status(entityType, entityId),
    includeAvailable,
  ],
  queryFn: () => getSharingStatus(entityType, entityId, includeAvailable),
  staleTime: 10_000,
});

/**
 * Mutation to update sharing on any entity.
 *
 * Uses optimistic updates: the cache is immediately updated with the
 * expected new state, and reverted on failure.
 */
export function useUpdateSharing() {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      payload,
    }: {
      entityType: EntityType;
      entityId: string;
      payload: SharePayload;
    }) => updateSharing(entityType, entityId, payload),

    onMutate: async (variables) => {
      const { entityType, entityId, payload } = variables;

      // Cancel outgoing refetches so they don't overwrite our optimistic update
      const queryKeyPrefix = SHARING_QUERY_KEYS.status(entityType, entityId);
      await queryClient.cancelQueries({ queryKey: queryKeyPrefix });

      // Snapshot current cache (with include_available=true, which is what the modal uses)
      const queryKey = [...queryKeyPrefix, true];
      const previousData =
        queryClient.getQueryData<SharingStatusResponse>(queryKey);

      // Optimistically update the cache
      if (previousData) {
        const optimistic = computeOptimisticStatus(previousData, payload);
        queryClient.setQueryData(queryKey, optimistic);
      }

      return { previousData, queryKey };
    },

    onError: (_err, _variables, context) => {
      // Rollback to snapshot on failure
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSuccess: (_data, variables) => {
      // Sharing an RMS view bumps its `updated_at`, the token its next save
      // must carry; the answer here is the sharing status, not the row, so
      // the row is fetched into the views cache (#12151), and the same row
      // into the dashboards list, whose copy the page's head and the list's
      // Visibility chip read (#15511).
      if (variables.entityType === "view") {
        void refreshCachedView(
          queryClient,
          orgId,
          Number(variables.entityId)
        ).then((fresh) => {
          if (fresh) setCachedDashboard(queryClient, orgId, fresh);
          // The row could not be read: let the list read it again, as the views cache does.
          else
            void queryClient.invalidateQueries({
              queryKey: dashboardsQueryKey(orgId),
            });
        });
      }
    },

    onSettled: (_data, _error, variables) => {
      // Refetch in the background to ensure consistency
      queryClient.invalidateQueries({
        queryKey: SHARING_QUERY_KEYS.status(
          variables.entityType,
          variables.entityId
        ),
      });
    },
  });
}

/**
 * Mutation to bulk-share multiple entities.
 * Invalidates all sharing caches on success (since many entities may change).
 */
export function useBulkShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkSharePayload) => bulkShare(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SHARING_QUERY_KEYS.all,
      });
    },
  });
}

/**
 * Mutation to email a shared item to people who already have access (#10815).
 *
 * Read-only with respect to sharing state — it grants nothing — so there is no
 * cache to invalidate.
 */
export function useNotifyShare() {
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      payload,
    }: {
      entityType: EntityType;
      entityId: string;
      payload: NotifySharePayload;
    }) => notifyShare(entityType, entityId, payload),
  });
}
